import React, { useState, useEffect } from 'react';
import { Button, Table, Space, Modal, Tag, message, Alert, Empty, Row, Col, Input, Radio, Typography } from 'antd';
import { NodeIndexOutlined, DeleteOutlined, RobotOutlined, GlobalOutlined, SyncOutlined } from '@ant-design/icons';
import apiClient from '../api';

const { Text } = Typography;

const InboundRoutingManager = ({ onUpdate }) => {
  const [routings, setRoutings] = useState([]);
  const [loading, setLoading] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);

  useEffect(() => {
    loadRoutings();
  }, []);

  const loadRoutings = async () => {
    setLoading(true);
    try {
      const response = await apiClient.get('/inbound-routing');
      if (response.data.success) {
        setRoutings(response.data.routings || []);
      }
    } catch (error) {
      console.error('Error loading inbound routings:', error);
      message.error('Failed to load routing data');
    } finally {
      setLoading(false);
    }
  };

  const handleModeChange = async (phoneDigits, newMode) => {
    try {
      const routing = routings.find(r => r.phoneDigits === phoneDigits);
      const payload = {
        phoneDigits,
        mode: newMode,
        webhookUrl: routing?.webhookUrl,
        leadid: routing?.leadid
      };
      
      const response = await apiClient.post('/inbound-routing', payload);
      if (response.data.success) {
        message.success(`Updated status for ${phoneDigits}`);
        loadRoutings();
      }
    } catch (error) {
      message.error('Failed to update routing mode');
      console.error(error);
    }
  };

  const handleDelete = async () => {
    if (!deleteTarget) return;
    try {
      // Assuming a delete endpoint exists or just setting it back to AI mode is the delete
      // Our API doesn't have a direct delete for inbound routing yet, so we'll just switch it to AI as a workaround or wait
      // In this system, just switching to AI and clearing webhook is effectively 'resetting'
      const response = await apiClient.post('/inbound-routing', {
        phoneDigits: deleteTarget.phoneDigits,
        mode: 'ai'
      });
      
      if (response.data.success) {
        message.success('Routing reset to AI mode');
        setDeleteTarget(null);
        loadRoutings();
      }
    } catch (error) {
      message.error('Failed to delete routing');
    }
  };

  const columns = [
    {
      title: 'Phone Number',
      dataIndex: 'phoneDigits',
      key: 'phoneDigits',
      render: (text) => (
        <Space>
          <Tag color="cyan" style={{ fontSize: 13, padding: '2px 10px' }}>{text}</Tag>
        </Space>
      ),
    },
    {
      title: 'Current Mode',
      dataIndex: 'mode',
      key: 'mode',
      render: (mode, record) => (
        <Radio.Group 
          value={mode} 
          size="small" 
          onChange={(e) => handleModeChange(record.phoneDigits, e.target.value)}
          buttonStyle="solid"
        >
          <Radio.Button value="ai">
            <Space><RobotOutlined /> AI Agent</Space>
          </Radio.Button>
          <Radio.Button value="webhook">
            <Space><GlobalOutlined /> Webhook</Space>
          </Radio.Button>
        </Radio.Group>
      ),
    },
    {
      title: 'Route Destination',
      key: 'destination',
      render: (_, record) => {
        if (record.mode === 'webhook') {
          return (
            <div style={{ maxWidth: 250, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
              <Text type="secondary" copyable>{record.webhookUrl || 'No URL set'}</Text>
            </div>
          );
        }
        return <Text type="secondary">Follows Bot Mappings</Text>;
      },
    },
    {
      title: 'Lead ID',
      dataIndex: 'leadid',
      key: 'leadid',
      render: (id) => id ? <Tag color="blue">{id}</Tag> : <Text type="disabled">-</Text>
    },
    {
      title: 'Last Activity',
      dataIndex: 'updatedAt',
      key: 'updatedAt',
      render: (date) => date ? new Date(date).toLocaleString() : '-'
    },
    {
      title: 'Actions',
      key: 'actions',
      width: 100,
      render: (_, record) => (
        <Button 
          type="text" 
          danger 
          icon={<DeleteOutlined />}
          onClick={() => setDeleteTarget(record)}
        />
      ),
    },
  ];

  return (
    <div className="premium-card-container">
      <div style={{ marginBottom: 24 }}>
        <Row gutter={16} align="middle">
          <Col flex="auto">
            <h2 style={{ margin: 0, display: 'flex', alignItems: 'center' }}>
              <NodeIndexOutlined style={{ marginRight: 12, color: '#6366f1' }} />
              Inbound Routing Center
            </h2>
            <p style={{ color: '#666', margin: '4px 0 0 0' }}>
              Manage how incoming messages from individual numbers are handled
            </p>
          </Col>
          <Col>
            <Button 
              icon={<SyncOutlined spin={loading} />} 
              onClick={loadRoutings}
            >
              Refresh
            </Button>
          </Col>
        </Row>
      </div>

      <Alert
        message="Intelligent Routing Control"
        description={
          <div>
            <p style={{ marginBottom: 8 }}>
              This panel controls whether a user gets the <strong>AI Agent</strong> or is forwarded to an <strong>External Webhook</strong>.
            </p>
            <ul style={{ marginBottom: 0, paddingLeft: 20 }}>
              <li><strong>AI Agent:</strong> The bot responds using your configured templates and functions.</li>
              <li><strong>Webhook:</strong> All messages from this user are forwarded to a third-party server (CRM, etc.).</li>
            </ul>
          </div>
        }
        type="info"
        showIcon
        style={{ marginBottom: 24, borderRadius: 12 }}
      />

      <Table 
        columns={columns} 
        dataSource={routings}
        rowKey="phoneDigits"
        loading={loading}
        pagination={{ pageSize: 10 }}
        className="premium-table"
        locale={{
          emptyText: (
            <Empty 
              description="No special routing rules found. All numbers are currently following default AI mappings."
              image={Empty.PRESENTED_IMAGE_SIMPLE}
            />
          )
        }}
      />

      <Modal
        title="Reset Routing"
        open={!!deleteTarget}
        onCancel={() => setDeleteTarget(null)}
        onOk={handleDelete}
        okText="Reset to AI"
        okButtonProps={{ danger: true }}
      >
        <p>Are you sure you want to reset the routing for <strong>{deleteTarget?.phoneDigits}</strong>? This will remove any webhook forwarding.</p>
      </Modal>
    </div>
  );
};

export default InboundRoutingManager;
